// src/contexts/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  // Fetch user data when token exists
  useEffect(() => {
    if (token) {
      fetchUserData();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchUserData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user data:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (credentialResponse) => {
    try {
      // ✅ Send the raw Google credential JWT — verified server-side, not decoded here
      const response = await axios.post(`${API_URL}/api/auth/google`, {
        credential: credentialResponse.credential
      });

      const { token: newToken, user: userData } = response.data;
      
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);
      
      return { success: true, user: userData };
    } catch (error) {
      console.error('Login failed:', error);
      return { success: false, error: error.response?.data?.error || 'Login failed' };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const refreshUserData = async () => {
    if (token) {
      await fetchUserData();
    }
  };

  // Save the preferred notification email (empty string clears it → falls back
  // to the Google login email). Updates local user state on success.
  const updateNotificationEmail = async (notificationEmail) => {
    try {
      const response = await axios.patch(
        `${API_URL}/api/auth/me/notification-email`,
        { notificationEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUser((prev) => (prev ? { ...prev, notificationEmail: response.data.notificationEmail } : prev));
      return { success: true, notificationEmail: response.data.notificationEmail };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Failed to save email' };
    }
  };

  // Toggle instant per-action email notifications. The scheduled digests are
  // sent regardless; this only controls the immediate per-action emails.
  const updateInstantEmails = async (instantEmails) => {
    try {
      const response = await axios.patch(
        `${API_URL}/api/auth/me/instant-emails`,
        { instantEmails },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUser((prev) => (prev ? { ...prev, instantEmails: response.data.instantEmails } : prev));
      return { success: true, instantEmails: response.data.instantEmails };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Failed to save preference' };
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    refreshUserData,
    updateNotificationEmail,
    updateInstantEmails,
    isAuthenticated: !!user,
    isSilver: user?.tier === 'silver',
    isGold: user?.tier === 'gold',
    isPlatinum: user?.tier === 'platinum',
    isRubber: user?.tier === 'rubber',
    canAddData: user?.tier === 'gold' || user?.tier === 'platinum' || user?.tier === 'rubber',
    canApprove: user?.tier === 'platinum' || user?.tier === 'rubber',
    canManageTiers: user?.tier === 'rubber',
    canAccessTasks: user?.canAccessTasks === true,
    isSalesTeam: user?.salesRole !== null && user?.salesRole !== undefined,
    isSalesManager: user?.salesRole === 'manager',
    isSalesRep: user?.salesRole === 'rep',
    canAccessInbox: user?.canAccessInbox === true,
    canAccessNfc: user?.canAccessNfc === true
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
